import * as _ from "lodash";
import { Component, OnInit, Input, AfterViewChecked, ChangeDetectorRef } from "@angular/core";
import { filter, take, takeUntil } from "rxjs/operators";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable } from "rxjs";
import { CookieService } from "ngx-cookie-service";

import { BaseComponent } from "../base.component";
import { Balance, UserDetails } from "../core-services/user/user.model";
import { UserStore } from "../core-services/user/user.store";
import { MenuComponent } from "../menu/menu.component";
import { PageStore } from "../core-services/page/page.store";
import { Button, MenuItemConfig } from "./header.models";
import { ModalsStore } from "../core-services/modals/modals.store";
import { MenuTypes } from "../menu/menu.models";
import { HeaderConfig } from "./header.models";
import { Languages, PrefixLangs } from "../core-services/core.models";
import { Jurisdication } from "../core-services/visitor/visitor.model";
import { PlatformService } from "../core-services/platform/platform.service";
import {
  BrandService,
  PayAndPlayConstants,
  PayAndPlayProvider,
  PayAndPlayService,
  PnpRequestTypes
} from '@whitehatgaming/shared';

export const HEADER_SELECTOR = "whg-header";

@Component({
  selector: HEADER_SELECTOR,
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"]
})
export class HeaderComponent extends BaseComponent implements OnInit, AfterViewChecked {
  @Input() loggedIn: string;

  headerConfig: HeaderConfig;
  loggedOutButtons: Button[];
  loggedInMenu: MenuItemConfig[];
  loggedOutMenu: MenuItemConfig[];
  isLoggedIn: boolean;
  userDetails$: Observable<UserDetails> = this.userStore.userDetails$.pipe(filter(x => !!x));
  balance: Balance;
  currentJurisdiction: string;
  languages = Languages;
  screenWidth: number;
  pnpLoginCalled: boolean = false;

  get menu(): MenuItemConfig[] {
    if (this.isLoggedIn) {
      return this.loggedInMenu;
    } else {
      return this.loggedOutMenu;
    }
  }

  constructor(
    public pageStore: PageStore,
    private userStore: UserStore,
    private modalService: NgbModal,
    private readonly modalsStore: ModalsStore,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private platformService: PlatformService,
    private brandService: BrandService,
    private payAndPlayService: PayAndPlayService
  ) {
    super(PrefixLangs.Header);
  }

  ngOnInit() {
    super.ngOnInit();
    this.config$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe((headerConfig: HeaderConfig) => {
      this.headerConfig = headerConfig;
      this.loggedInMenu = headerConfig.menu.loggedIn;
      this.loggedOutMenu = headerConfig.menu.loggedOut;
      this.loggedOutButtons = headerConfig.button.loggedOut;
    });
    this.payAndPlayService.setUserCountry(this.countryCode);
    this.isLoggedIn = JSON.parse(this.loggedIn);

    this.pageStore.page$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      if (page === 'login' && this.loggedOutButtons) {
        this.loggedOutButtons = this.loggedOutButtons.filter(item => !item.url.startsWith('/login'));
      }
    });

    /*
    * We want to change language by default for Finnish users
    * The user should still have the ability to change language back
    */
    const isDefaultLangSet: string = this.cookieService.get('defaultLangSet');
    if (
      this.countryCode === "FI" &&
      this.coreService.getCurrentLangFromCookie() !== 'fi' &&
      isDefaultLangSet !== 'true'
    ) {
      this.cookieService.set('defaultLangSet', 'true');
      this.coreService.switchLanguage(Languages.FI);
    }

    this.coreService.clearUserDetails();
  }

  ngAfterViewChecked() {
    this.screenWidth = this.coreService.getScreenWidthFromWindow();
    this.cdr.detectChanges();
  }

  openMenu(): void {
    this.isLoggedIn ? this.modalsStore.changeMenuModalConfig(MenuTypes.LoggedIn) : this.modalsStore.changeMenuModalConfig(MenuTypes.LoggedOut);
    this.modalService.open(MenuComponent, {
      ariaLabelledBy: "whg-menu-sections-title",
      windowClass: "whg-menu-sections-modal",
    });
  }

  hideHomeOnLoggedIn(link: string): boolean {
    return this.isLoggedIn && link === '/' ? false : true;
  }

  hideVipOnUkgc(link: string): boolean {
    return this.jurisdiction === Jurisdication.UKGC && link === '/vip';
  }

  isMenuOnRegisterPage(): boolean {
    return this.coreService.isCurrentPathContains('create-account');
  }

  isMenuOnLoginPage(): boolean {
    return this.coreService.isCurrentPathContains('login');
  }

  isMenuOnResponsibleGamblingPage(): boolean {
    return (this.coreService.isCurrentPathContains('playerprotection') || this.coreService.isCurrentPathContains('account') ? true : false);
  }

  isMenuOnLandingPage(): boolean {
    return !this.isLoggedIn && this.coreService.isCurrentPathContains('landing');
  }

  fixMenuPositioningForPages(): boolean {
    return this.coreService.isCurrentPathContains('activate&token')
      || this.coreService.isCurrentPathContains('page=marketingprefs')
      || this.coreService.isCurrentPathContains('page=messages&message')
      || this.coreService.isCurrentPathContains('landing');
  }

  isCurrentPageOnPnP(): boolean {
    let isContains = false;
    Object.values(PayAndPlayProvider).forEach((provider: string) => {
      isContains = isContains || this.coreService.isCurrentPathContains(provider);
    })
    return isContains;
  }

  ifFinlandChangeUrls(pageUrl: string): string {
    const currentProviderConfiguration = this.payAndPlayService.getCurrentProvider();
    if (currentProviderConfiguration) {
      if (pageUrl === '/register') {
        return '/' + currentProviderConfiguration.provider + '/deposit';
      } else {
        if (!this.pnpLoginCalled) {
          this.payAndPlayLogin();
        }
        return '/' + currentProviderConfiguration.provider + '/bankid';
      }
    } else {
      return pageUrl;
    }
  }

  payAndPlayLogin(): void {
    this.pnpLoginCalled = true;
    let pnpPayload = this.payAndPlayService.createPnPRequestPayload();
    const currentProviderConfiguration = this.payAndPlayService.getCurrentProvider();
    this.payAndPlayService.initPayAndPlayProviderWithType(PnpRequestTypes.LOGIN_VIA, pnpPayload, this.coreService.platformBaseUrl)
      .pipe(takeUntil(this.destroy$), take(1)).subscribe(data => {
        if (data['code']) {
          localStorage.setItem('errorCode', data['code']);
        }
        localStorage.setItem(PayAndPlayConstants.frameUrl, '"' + data['url'] + '"');
        // check if really need this one
        window.location.href = '/' + currentProviderConfiguration.provider + '/bankid';
      },
        error => {
          console.warn(`Error: ${error.message}`);
        }
      );
  }

  getMenuItemActive(url: string): boolean {
    if (url !== '/') {
      let urlMatches: boolean = this.coreService.isCurrentPathContains(url);
      /*
      * Checks for legacy page route (example: /index.php?page=....)
      * */
      const legacyUrlPageParam: string = this.coreService.getParamValueQueryString('page');
      if (legacyUrlPageParam && (`/${legacyUrlPageParam}` === url)) {
        urlMatches = true;
      }
      return urlMatches;
    } else {
      const urlPath: string = this.coreService.getUrlPath();
      return url === urlPath;
    }
  }

  checkMenuContdition(): boolean {
    return !this.isLoggedIn && !this.isMenuOnLoginPage() && !this.isMenuOnResponsibleGamblingPage() && !this.isCurrentPageOnPnP();
  }

}
